<template>
  <!-- The field and the one strip of the connection under it, 8 apart (handoff `01`): while something
       is typed, the search's — what it searched, as of when — otherwise the list's, the screen's own. -->
  <div class="head" @focusin="entered" @focusout="left">
    <SearchField
      :model-value="modelValue"
      :label="t('advice.search.label')"
      :placeholder="t('advice.search.placeholder')"
      clearable
      @update:model-value="$emit('update:modelValue', $event)"
    />
    <StatusStrip
      v-if="phase === 'memory' && fetchedAt"
      kind="offline"
      :text="t('advice.search.offline', { when: when(fetchedAt) })"
    />
    <slot v-else-if="phase === 'idle'" name="strip" />
  </div>

  <template v-if="phase !== 'idle'">
    <AdviceSkeleton v-if="phase === 'loading'" />

    <ScreenState
      v-else-if="phase === 'error'"
      kind="error"
      inline
      :title="t('advice.error.title')"
      :body="t('advice.search.error_body')"
      @retry="retry"
    />

    <!-- No action: «Предложить товар» is the entry of a purchase's, and here nothing was bought
         (handoff `01`, 1e). -->
    <ScreenState
      v-else-if="found.length === 0"
      :class="{ stale }"
      kind="empty"
      :icon="IconNotFound"
      inline
      :title="t('advice.search.empty.title', { query: answered })"
      :body="
        phase === 'memory' ? t('advice.search.empty.body_offline') : t('advice.search.empty.body')
      "
    />

    <div v-else class="found" :class="{ stale }">
      <!-- Rows, and none of them close: the server's word (MOL-46), said above them. -->
      <p v-if="phase === 'far'" class="miss">
        {{ t('advice.search.empty.title', { query: answered }) }}
      </p>

      <AdviceGroup v-if="groups.take.length > 0" level="take">
        <AdviceTakeCard
          v-for="row in groups.take"
          :key="row.itemId"
          :row="row"
          :scope="scope"
          @edit="$emit('edit', row, scope)"
        />
      </AdviceGroup>

      <AdviceGroup v-if="groups.if_cheap.length > 0" level="if_cheap">
        <AdviceCheapRow
          v-for="row in groups.if_cheap"
          :key="row.itemId"
          :row="row"
          :scope="scope"
          @edit="$emit('edit', row, scope)"
        />
      </AdviceGroup>

      <!-- No price and no place here either: the row has no field for them (MOL-31, Р-8). -->
      <AdviceGroup v-if="groups.never.length > 0" level="never">
        <AdviceNeverRow
          v-for="row in groups.never"
          :key="row.itemId"
          :row="row"
          :scope="scope"
          @edit="$emit('edit', row, scope)"
        />
      </AdviceGroup>

      <AdviceGroup v-if="groups.unrated.length > 0" level="unrated">
        <!-- No meta: «Ещё не оценивали» is the caption's already (С-9 of MOL-118). No chevron: the
             word in the tail opens the sheet, as a card of a group does with none. -->
        <AppCard list>
          <ListRow
            v-for="item in groups.unrated"
            :key="item.itemId"
            :title="item.name"
            wrap
            @click="$emit('rate', item, scope)"
          >
            <template #tail
              ><span class="rate">{{ t('advice.search.rate') }}</span></template
            >
          </ListRow>
        </AppCard>
      </AdviceGroup>

      <p v-if="phase === 'memory'" class="note more">{{ t('advice.search.offline_more') }}</p>
    </div>
  </template>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, toRef, watch } from 'vue'
import type { PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import IconNotFound from '~icons/mdi/magnify'
import type { AdviceFound, AdviceResponse, AdviceRow, AdviceScope } from '@molvia/model'
import AdviceCheapRow from '@/components/AdviceCheapRow.vue'
import AdviceGroup from '@/components/AdviceGroup.vue'
import AdviceNeverRow from '@/components/AdviceNeverRow.vue'
import AdviceSkeleton from '@/components/AdviceSkeleton.vue'
import AdviceTakeCard from '@/components/AdviceTakeCard.vue'
import AppCard from '@/components/AppCard.vue'
import ListRow from '@/components/ListRow.vue'
import ScreenState from '@/components/ScreenState.vue'
import SearchField from '@/components/SearchField.vue'
import StatusStrip from '@/components/StatusStrip.vue'
import type { CheapRow, NeverRow, TakeRow } from '@/components/adviceRow'
import { useAdviceSearch } from '@/composables/useAdviceSearch'
import { useAnnouncer } from '@/composables/useAnnouncer'
import { answeredAt } from '@/days'

/** The found items laid out by group, each in the order the search gave it. */
interface Found {
  readonly take: TakeRow[]
  readonly if_cheap: CheapRow[]
  readonly never: NeverRow[]
  readonly unrated: AdviceFound[]
}

function laidOut(found: readonly AdviceFound[]): Found {
  const groups: Found = { take: [], if_cheap: [], never: [], unrated: [] }
  for (const item of found) {
    const row = item.advice
    if (row === null) groups.unrated.push(item)
    else if (row.level === 'take') groups.take.push(row)
    else if (row.level === 'if_cheap') groups.if_cheap.push(row)
    else groups.never.push(row)
  }
  return groups
}

/**
 * The search on «Что брать» (MOL-128, handoff `01`): a field at the top of the list, and what it
 * finds in the whole catalogue laid out by the same groups and the same shapes as the list — a
 * card, a row, a line — then «Ещё не оценивали» last. The groups decide the order; inside one,
 * the search does.
 *
 * Every row is the server's (В-1). «Не брать нигде» comes with no price and no place, because its
 * row has no field for them, here as in the list. No «Предложить товар», and nothing on the page
 * could ever be promoted: there is no field to promote it by.
 */
export default defineComponent({
  name: 'AdviceSearch',
  components: {
    AdviceCheapRow,
    AdviceGroup,
    AdviceNeverRow,
    AdviceSkeleton,
    AdviceTakeCard,
    AppCard,
    ListRow,
    ScreenState,
    SearchField,
    StatusStrip,
  },
  props: {
    modelValue: { type: String, required: true },
    /** The list the phone keeps: searched on the phone with no connection (В-3). */
    remembered: { type: Object as PropType<AdviceResponse | null>, default: null },
    fetchedAt: { type: Date as PropType<Date | null>, default: null },
    /** The screen's day (`useLocalDay`), which the list's age is said from (MOL-329). */
    today: { type: String, required: true },
    /** Raised after a verdict is saved: the rows on screen are asked for again. */
    refreshes: { type: Number, default: 0 },
  },
  emits: {
    'update:modelValue': (text: string) => typeof text === 'string',
    /** With the scope of the answer the row came in: the list's may be another (review Р-13, Е). */
    edit: (row: AdviceRow, scope: AdviceScope) => typeof row === 'object' && !!scope,
    rate: (item: AdviceFound, scope: AdviceScope) => typeof item === 'object' && !!scope,
    /**
     * The focus came into the field or its «Очистить», or left them: «Что брать» keeps it then. Left
     * by a press, it is told once the press is over; left for another app, not at all.
     */
    focused: (inside: boolean) => typeof inside === 'boolean',
  },
  setup(props, { emit }) {
    const { t, locale } = useI18n()
    const search = useAdviceSearch(toRef(props, 'modelValue'), () => props.remembered)
    const { phase, found, stale, answered } = search
    const announce = useAnnouncer()

    // After a save the rows on screen are asked for again quietly, dimmed rather than taken away
    // under the sheet that is closing over them (review Р-3, adversarial Г).
    watch(
      () => props.refreshes,
      () => {
        search.refresh()
      },
    )

    // Read out once per answer, and never a dimmed one: it answers the text before (MOL-23, Р-10).
    // Nothing found is not read here: the empty block says it itself (review Р-8).
    let withdraw: (() => void) | undefined
    watch([phase, found, stale], ([next, rows, dimmed]) => {
      withdraw?.()
      withdraw = undefined
      if (dimmed || rows.length === 0 || !['ready', 'far', 'memory'].includes(next)) return
      withdraw = announce?.(
        next === 'far'
          ? t('advice.search.empty.title', { query: answered.value })
          : t('item.results_announced', { n: rows.length }, rows.length),
      )
    })
    onUnmounted(() => {
      withdraw?.()
    })

    // A press takes the focus out of the field as it lands, and the field over a failure or a
    // newcomer goes with it (В-4 «а») — told then, it went before the finger lifted, everything
    // under it rose by its height, and the release landed on something else: the first tap on
    // «Повторить» or a step of the cycle did nothing (adversarial В1). So the leaving is told a task
    // after the release, past its click (owner's В-5 «а»); a focus moved by the keys, at once.
    let pressed = false
    let leaving = false
    const press = () => {
      pressed = true
    }
    const lift = () => {
      setTimeout(() => {
        pressed = false
        if (!leaving) return
        leaving = false
        emit('focused', false)
      })
    }
    onMounted(() => {
      document.addEventListener('mousedown', press, true)
      document.addEventListener('mouseup', lift, true)
    })
    onUnmounted(() => {
      document.removeEventListener('mousedown', press, true)
      document.removeEventListener('mouseup', lift, true)
      leaving = false
    })
    function entered(): void {
      leaving = false
      emit('focused', true)
    }
    function left(): void {
      // The page lost the system's focus — another app, to copy a name — and the field keeps it:
      // the focus comes back to it with the person.
      if (!document.hasFocus()) return
      if (pressed) leaving = true
      else emit('focused', false)
    }

    return {
      t,
      IconNotFound,
      phase,
      found,
      stale,
      answered,
      scope: search.scope,
      groups: computed(() => laidOut(found.value)),
      retry: search.retry,
      entered,
      left,
      when: (at: Date) => answeredAt(at, locale.value, props.today),
    }
  },
})
</script>

<style scoped lang="scss">
/* `.ghost-head` of «Что брать» draws this still over the skeleton: a change here goes there too. */
.head {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-6);
}

.note,
.miss {
  margin: 0 0 var(--space-3);
  color: var(--text-muted);
  font-size: var(--text-footnote);
  overflow-wrap: anywhere;
}

.more {
  margin: var(--space-4) 0 0;
}

.found,
.stale {
  transition: opacity var(--dur) var(--ease-out);
}

.stale {
  opacity: var(--opacity-stale);
}

@media (prefers-reduced-motion: reduce) {
  .found,
  .stale {
    transition: none;
  }
}

.rate {
  color: var(--accent-ink);
  font-size: var(--text-callout);
  font-weight: var(--weight-medium);
}
</style>
